import { useCallback, useEffect, useState } from 'react';
import { type CountryIso2, defaultCountries, FlagImage } from 'react-international-phone';
import { twMerge } from 'tailwind-merge';

import { MagnifyingGlassIcon, t, useDebounce } from '../../../../index';
import { useOnClickOutside } from '../../../hooks';
import { TextInput } from '../TextInput';

type Props = {
  country: CountryIso2;
  setCountry: (_country: CountryIso2) => void;
  className?: string;
};

export const CountryPicker = ({ country, className, setCountry }: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  useEffect(() => {
    if (isVisible === false) {
      setFilter('');
    }
  }, [isVisible]);

  return (
    <div className={twMerge('relative', className)}>
      <button
        type="button"
        onClick={() => setIsVisible(true)}
        className="flex items-center justify-center h-full px-3 border-r border-border-secondary"
      >
        <FlagImage className="w-6" iso2={country} />
      </button>
      {isVisible ? (
        <div
          ref={ref}
          className="absolute top-14 left-0 z-10 bg-surface-primary rounded-md w-60 border border-border-primary shadow-md"
        >
          <div className="p-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MagnifyingGlassIcon />
              </div>

              <TextInput
                type="text"
                placeholder={t('pwa.common.search-country')}
                value={filter}
                onChange={e => setFilter(e.currentTarget.value)}
              />
            </div>
          </div>
          <ul
            className="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700 dark:text-gray-200"
            aria-labelledby="dropdownSearchButton"
          >
            {defaultCountries
              .filter(c => c[0].toLowerCase().includes(debouncedFilter.toLowerCase()))
              .map(country => (
                <li key={country[1]}>
                  <button
                    type="button"
                    className="w-full flex items-center pl-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => {
                      setCountry(country[1]);
                      setIsVisible(false);
                    }}
                  >
                    <FlagImage className="w-6" iso2={country[1]} />
                    <span className="py-2 ml-2 text-sm font-medium text-gray-900 rounded-sm dark:text-gray-300 text-left">
                      {country[0]} +{country[2]}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
